import { useEffect, useState } from "react";
import { TrendingUp, ExternalLink, RefreshCw, Music2 } from "lucide-react";
import api from "@/lib/api";

export default function Top40Section() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const fetchChart = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/charts/top40-house");
      setData(data);
      setErr("");
    } catch (e) {
      setErr("Couldn't load the chart right now.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChart();
  }, []);

  const tracks = data?.tracks || [];
  const fetchedAt = data?.fetched_at
    ? new Date(data.fetched_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })
    : "";

  return (
    <section className="w-full max-w-[1080px] mx-auto mt-16 mb-8 px-4" data-testid="top40-section">
      {/* Section header */}
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp size={16} className="text-[var(--brand-purple-light)]" />
            <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-[var(--brand-purple-light)]">
              Weekly Chart · Powered by Last.fm
            </span>
          </div>
          <h2 className="font-display text-3xl md:text-4xl font-bold leading-tight">
            Top <span className="text-[var(--neon-cyan)]">40</span> House Remixes
          </h2>
          <p className="text-[var(--text-muted)] text-sm mt-1 font-mono">
            of the week · refreshed every 6h · {tracks.length} tracks
          </p>
        </div>
        <div className="flex items-center gap-2">
          {fetchedAt && (
            <span className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]" data-testid="top40-fetched-at">
              updated {fetchedAt}
            </span>
          )}
          <button
            onClick={fetchChart}
            disabled={loading}
            className="btn-ghost flex items-center gap-1.5"
            data-testid="top40-refresh-button"
            aria-label="Refresh chart"
          >
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {/* Body */}
      {loading && !data ? (
        <div className="player-card p-8 text-center" data-testid="top40-loading">
          <Music2 size={28} className="mx-auto mb-3 text-[var(--text-muted)] animate-pulse" />
          <p className="text-[var(--text-muted)] font-mono text-sm">Crate-digging the weekly charts...</p>
        </div>
      ) : err ? (
        <div className="player-card p-6 text-center" data-testid="top40-error">
          <p className="text-[#ff5566] text-sm font-mono">{err}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-2" data-testid="top40-list">
          {tracks.map((t) => (
            <a
              key={`${t.position}-${t.title}`}
              href={t.lastfm_url || "#"}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`top40-track-${t.position}`}
              className="group flex items-center gap-3 p-2.5 rounded-lg bg-[#0d0d16] border border-[var(--border-soft)] hover:border-[var(--border-cyan)] hover:bg-[#11111d] transition-all"
            >
              {/* Rank */}
              <span
                className={`font-display font-black text-2xl w-9 text-center flex-shrink-0 ${
                  t.position <= 3
                    ? "text-[var(--neon-cyan)]"
                    : t.position <= 10
                    ? "text-white"
                    : "text-[var(--text-muted)]"
                }`}
              >
                {String(t.position).padStart(2, "0")}
              </span>

              {/* Cover art */}
              <div className="flex-shrink-0 w-12 h-12 rounded overflow-hidden bg-gradient-to-br from-[#1a1a28] to-[#0a0a14] border border-[var(--border-soft)]">
                {t.image_url ? (
                  <img
                    src={t.image_url}
                    alt=""
                    loading="lazy"
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.style.display = "none"; }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Music2 size={16} className="text-[var(--text-muted)]" />
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-white truncate group-hover:text-[var(--neon-cyan)] transition-colors">
                  {t.title}
                </p>
                <p className="text-xs text-[var(--text-muted)] font-mono truncate">
                  {t.artist}
                </p>
              </div>

              <ExternalLink
                size={14}
                className="text-[var(--text-muted)] group-hover:text-[var(--neon-cyan)] flex-shrink-0 opacity-0 group-hover:opacity-100 transition-all"
              />
            </a>
          ))}
        </div>
      )}

      {/* Footer credit */}
      <p className="text-center text-[10px] font-mono uppercase tracking-[0.3em] text-[var(--text-muted)] mt-8">
        Chart data · Last.fm · TheAudioDB · Curated by <span className="text-[var(--brand-purple-light)]">JAVI G</span>
      </p>
    </section>
  );
}
